<script setup>
import { Head, Link } from '@inertiajs/inertia-vue3'
import LandingPageLayout from '@/Layouts/LandingPageLayout.vue'
import {
    ArrowPathIcon,
    InboxIcon,
    CloudArrowUpIcon,
    CogIcon,
    LockClosedIcon,
    ServerIcon,
    ShieldCheckIcon,
    SparklesIcon,
    UserGroupIcon,
    CheckIcon,
} from '@heroicons/vue/24/outline'

const stats = [
    { label: 'Years of combined experience', value: '30+' },
    //{ label: 'Most words in a hand typed entry', value: '6,067' },
]
const values = [
    {
        name: 'Set the bar high',
        description:
            'We maintain standards that make us the leaders in the industry for caring for your logbooks and quickly accessing your critical data.',
    },
    {
        name: 'Serve all of GA',
        description:
            'No plane is too small or project too simple. We strive to provide professional level maintenance tracking and services to even the smallest and simplest of aircraft.',
    },
    {
        name: 'Always learning',
        description:
            'At PLANELOGIX, our customers are our greatest asset, and their feedback is ingrained in the DNA and code of our product.',
    },
]

const team = [
    {
        name: 'Rob Wilkes',
        role: 'Co-Founder',
        imageUrl: 'https://logixpublic.s3.amazonaws.com/about/rob.jpg',
    },
    {
        name: 'Will Goldstein',
        role: 'Co-Founder',
        imageUrl:
            'https://logixpublic.s3.amazonaws.com/about/will_cropped_opt.jpg',
    },
    {
        name: 'Colin Law',
        role: 'Aircraft Document Specialist',
        imageUrl: 'https://logixpublic.s3.amazonaws.com/about/colin.png',
    },
    {
        name: 'Claudia Batts',
        role: 'Account Support and Customer Success Manager',
        imageUrl: 'https://logixpublic.s3.amazonaws.com/about/claudia.jpg',
    },
]

const timeline = [
    {
        name: 'Founded',
        description:
            "Logbooks for Rob's SR20 were lost by the shop and a lightbulb went off! (Don't worry, they were later found)",
        date: 'Jan 2014',
        dateTime: '2014-01',
    },
    {
        name: 'First EAA AirVenture',
        description:
            'We attended our first major tradeshow and instantly garnered attention.',
        date: 'July 2014',
        dateTime: '2014-07',
    },
    {
        name: 'First Major Relaunch',
        description:
            'We relaunched our web app which was working for most, but needed to go to the next level.',
        date: 'Nov 2019',
        dateTime: '2019-11',
    },
    {
        name: 'Partnership with Aircraft Spruce',
        description: 'We became a strategic partner of Aircraft Spruce.',
        date: 'May 2020',
        dateTime: '2020-05',
    },
    {
        name: 'New Growth Trajectory',
        description:
            'On the heels of a new website and Spruce partnership, our growth began to go vertical',
        date: 'June 2020',
        dateTime: '2020-06',
    },
    {
        name: '100%+ Growth',
        description: 'We more than double our paying user base this year!',
        date: 'Dec 2022',
        dateTime: '2022-12',
    },
    {
        name: 'Continuing to Innovate',
        description:
            'Coming off of triple digit growth in 2022, and recognizing the need for an even better user experience, Rob and Will relaunched (again!) a new version of their web app using the latest in web technologies and a combined 20 years experience in web development and aviation.',
        date: 'July 2023',
        dateTime: '2023-07',
    },
]
</script>

<template>
    <LandingPageLayout title="Company">
        <div class="isolate overflow-hidden">
            <div class="flow-root bg-gray-900 py-16 sm:pt-32 lg:pb-30">
                <div class="mx-auto max-w-7xl px-6 lg:px-8">
                    <div class="relative z-10">
                        <h1
                            class="mx-auto max-w-4xl text-center text-5xl font-bold tracking-tight text-white"
                        >
                            About Us
                        </h1>
                        <p
                            class="mx-auto mt-4 max-w-2xl text-center text-lg leading-8 text-white/60"
                        >
                            Learn about our story and who we are
                        </p>
                    </div>
                    <div
                        class="relative mx-auto mt-10 grid max-w-md grid-cols-1 gap-y-8 lg:mx-0 lg:-mb-14 lg:max-w-none lg:grid-cols-2"
                    >
                        <svg
                            viewBox="0 0 1208 1024"
                            aria-hidden="true"
                            class="absolute -bottom-48 left-1/2 h-[64rem] -translate-x-1/2 translate-y-1/2 [mask-image:radial-gradient(closest-side,white,transparent)] lg:-top-48 lg:bottom-auto lg:translate-y-0"
                        >
                            <ellipse
                                cx="604"
                                cy="512"
                                fill="url(#d25c25d4-6d43-4bf9-b9ac-1842a30a4867)"
                                rx="604"
                                ry="512"
                            />
                            <defs>
                                <radialGradient
                                    id="d25c25d4-6d43-4bf9-b9ac-1842a30a4867"
                                >
                                    <stop stop-color="#111827" />
                                    <stop offset="1" stop-color="#01b2ff" />
                                </radialGradient>
                            </defs>
                        </svg>
                        <div
                            class="hidden lg:absolute lg:inset-x-px lg:bottom-0 lg:top-4 lg:block lg:rounded-t-2xl lg:bg-gray-800/80 lg:ring-1 lg:ring-white/10"
                            aria-hidden="true"
                        />
                    </div>
                </div>
            </div>
        </div>
        <!-- Digital only or Digital Hardcopy -->
        <div class="isolate">
            <!-- Hero section -->
            <div class="relative isolate -z-10">
                <svg
                    class="absolute inset-x-0 top-0 -z-10 h-[64rem] w-full stroke-gray-200 [mask-image:radial-gradient(32rem_32rem_at_center,white,transparent)]"
                    aria-hidden="true"
                >
                    <defs>
                        <pattern
                            id="1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84"
                            width="200"
                            height="200"
                            x="50%"
                            y="-1"
                            patternUnits="userSpaceOnUse"
                        >
                            <path d="M.5 200V.5H200" fill="none" />
                        </pattern>
                    </defs>
                    <svg x="50%" y="-1" class="overflow-visible fill-gray-50">
                        <path
                            d="M-200 0h201v201h-201Z M600 0h201v201h-201Z M-400 600h201v201h-201Z M200 800h201v201h-201Z"
                            stroke-width="0"
                        />
                    </svg>
                    <rect
                        width="100%"
                        height="100%"
                        stroke-width="0"
                        fill="url(#1f932ae7-37de-4c0a-a8b0-a6e3b4d44b84)"
                    />
                </svg>
                <div
                    class="absolute left-1/2 right-0 top-0 -z-10 -ml-24 transform-gpu overflow-hidden blur-3xl lg:ml-24 xl:ml-48"
                    aria-hidden="true"
                >
                    <div
                        class="aspect-[801/1036] w-[50.0625rem] bg-gradient-to-tr from-[#edf4ff] to-[#599fff] opacity-30"
                        style="
                            clip-path: polygon(
                                63.1% 29.5%,
                                100% 17.1%,
                                76.6% 3%,
                                48.4% 0%,
                                44.6% 4.7%,
                                54.5% 25.3%,
                                59.8% 49%,
                                55.2% 57.8%,
                                44.4% 57.2%,
                                27.8% 47.9%,
                                35.1% 81.5%,
                                0% 97.7%,
                                39.2% 100%,
                                35.2% 81.4%,
                                97.2% 52.8%,
                                63.1% 29.5%
                            );
                        "
                    />
                </div>
                <div class="overflow-hidden">
                    <div
                        class="mx-auto max-w-7xl px-6 pb-32 pt-36 sm:pt-60 lg:px-8 lg:pt-32"
                    >
                        <div
                            class="mx-auto max-w-2xl gap-x-14 lg:mx-0 lg:flex lg:max-w-none lg:items-center"
                        >
                            <div
                                class="w-full max-w-xl lg:shrink-0 xl:max-w-2xl"
                            >
                                <h1
                                    class="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl"
                                >
                                    We’re changing the way people manage their
                                    aircraft.
                                </h1>
                                <p
                                    class="relative mt-6 text-lg leading-8 text-gray-600 sm:max-w-md lg:max-w-none"
                                >
                                    We believe the General Aviation community is
                                    long overdue for an overhaul in the way
                                    maintenance records and documents are
                                    stored, retrieved, and utilized. We started
                                    this company through the need to protect and
                                    access the data within our logbooks, and
                                    have since had the pleasure of working with
                                    thousands of people to improve everything
                                    about owning an aircraft.
                                </p>
                            </div>
                            <div
                                class="mt-14 flex justify-end gap-8 sm:-mt-44 sm:justify-start sm:pl-20 lg:mt-0 lg:pl-0"
                            >
                                <div
                                    class="ml-auto w-44 flex-none space-y-8 pt-32 sm:ml-0 sm:pt-80 lg:order-last lg:pt-36 xl:order-none xl:pt-80"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://logixpublic.s3.amazonaws.com/about/IMG_6964-min.jpg"
                                            alt=""
                                            class="w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                                <div
                                    class="mr-auto w-44 flex-none space-y-8 sm:mr-0 sm:pt-52 lg:pt-36"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://logixpublic.s3.amazonaws.com/partnerships/Case_.png"
                                            alt=""
                                            class="w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                    <div class="relative">
                                        <img
                                            src="https://logixpublic.s3.amazonaws.com/about/IMG_4859-min.jpg"
                                            alt=""
                                            class="w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                                <div
                                    class="w-44 flex-none space-y-8 pt-32 sm:pt-0"
                                >
                                    <div class="relative">
                                        <img
                                            src="https://logixpublic.s3.amazonaws.com/about/IMG_0882-min.jpg"
                                            alt=""
                                            class="w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                    <div class="relative">
                                        <img
                                            src="https://logixpublic.s3.amazonaws.com/about/IMG_0892-min.jpg"
                                            alt=""
                                            class="w-full rounded-xl bg-gray-900/5 object-cover shadow-lg"
                                        />
                                        <div
                                            class="pointer-events-none absolute inset-0 rounded-xl ring-1 ring-inset ring-gray-900/10"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- Content section -->
            <div class="mx-auto mt-12 max-w-7xl px-6 sm:mt-8 lg:px-8 xl:mt-8">
                <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        About PLANELOGIX
                    </h2>
                    <div
                        class="mt-6 flex flex-col gap-x-8 gap-y-20 lg:flex-row"
                    >
                        <div class="lg:w-full lg:max-w-2xl lg:flex-auto">
                            <p class="text-xl leading-8 text-gray-600">
                                PLANELOGIX is co-founded, majority owned, and
                                entirely built and coded by Rob Wilkes and Will
                                Goldstein, two lifelong best friends who share a
                                passion for entrepreneurship, aviation, and web
                                development. Both Rob and Will left a
                                finance/Wall Street background to teach
                                themselves how to code in order to fulfill their
                                shared vision of reshaping the way aircraft
                                ownership is managed. Beginning as a simple
                                interface in 2014 while Rob and Will were both
                                in their 20s, it has since evolved into a
                                powerful and sophisticated platform utilizing
                                the latest technologies in web development. Now
                                for the (abridged) full story...
                            </p>
                            <div
                                class="mt-10 max-w-xl text-base leading-7 text-gray-700"
                            >
                                <p>
                                    Rob discovered through his aircraft
                                    ownership experience that there simply was
                                    no aircraft management service available to
                                    him for a reasonable price. After months of
                                    thinking, planning and tinkering, PLANELOGIX
                                    was born through in-house development.
                                </p>
                                <p class="mt-10">
                                    Since our beta launch at Sun 'N' Fun in the
                                    Spring of 2014, we have listened to our
                                    customers to build the system they want and
                                    need. We will continue to listen to our
                                    customer's needs and suggestions because we
                                    believe that our customer is our strongest
                                    asset and ally.
                                </p>
                                <p class="mt-10">
                                    We see first hand that having a transparent
                                    picture of an aircraft and its maintenance
                                    history is critical to any current owner,
                                    prospective buyer, broker, insurance agency,
                                    bank and just about anyone else who sets
                                    foot inside the aircraft.
                                </p>
                                <p class="mt-10">
                                    We provide an efficient service with an
                                    emphasis on maximizing quality. Accuracy is
                                    paramount, and whether we are converting
                                    logbooks for a pre-buy inspection, a
                                    post-buy service, a present owner, or a
                                    potential future owner, we always put our
                                    client’s interest before our own.
                                </p>
                                <p class="mt-10">
                                    Join us and change how you manage your
                                    airplane forever.
                                </p>
                            </div>
                        </div>
                        <div class="lg:flex lg:flex-auto lg:justify-center">
                            <dl class="w-64 space-y-8 xl:w-80">
                                <div
                                    v-for="stat in stats"
                                    :key="stat.label"
                                    class="flex flex-col-reverse gap-y-4"
                                >
                                    <dt
                                        class="text-base leading-7 text-gray-600"
                                    >
                                        {{ stat.label }}
                                    </dt>
                                    <dd
                                        class="text-5xl font-semibold tracking-tight text-gray-900"
                                    >
                                        {{ stat.value }}
                                    </dd>
                                </div>
                                <ul
                                    role="list"
                                    class="mx-auto mt-20 grid max-w-2xl grid-cols-2 gap-x-8 gap-y-16 text-center"
                                >
                                    <li
                                        v-for="person in team"
                                        :key="person.name"
                                    >
                                        <img
                                            class="mx-auto h-24 w-24 rounded-full"
                                            :src="person.imageUrl"
                                            alt=""
                                        />
                                        <h3
                                            class="mt-6 text-base font-semibold leading-7 tracking-tight text-gray-900"
                                        >
                                            {{ person.name }}
                                        </h3>
                                        <p
                                            class="text-sm leading-6 text-gray-600"
                                        >
                                            {{ person.role }}
                                        </p>
                                    </li>
                                </ul>
                            </dl>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Timeline section -->
            <div class="mx-auto mt-12 max-w-7xl px-6 sm:mt-0 lg:px-8 xl:mt-8">
                <div
                    class="mx-auto grid max-w-2xl grid-cols-1 gap-8 overflow-hidden lg:mx-0 lg:max-w-none lg:grid-cols-4"
                >
                    <div v-for="item in timeline" :key="item.name">
                        <time
                            :datetime="item.dateTime"
                            class="flex items-center text-sm font-semibold leading-6 text-indigo-600"
                        >
                            <svg
                                viewBox="0 0 4 4"
                                class="mr-4 h-1 w-1 flex-none"
                                aria-hidden="true"
                            >
                                <circle
                                    cx="2"
                                    cy="2"
                                    r="2"
                                    fill="currentColor"
                                />
                            </svg>
                            {{ item.date }}
                            <div
                                class="absolute -ml-2 h-px w-screen -translate-x-full bg-gray-900/10 sm:-ml-4 lg:static lg:-mr-6 lg:ml-8 lg:w-auto lg:flex-auto lg:translate-x-0"
                                aria-hidden="true"
                            />
                        </time>
                        <p
                            class="mt-6 text-lg font-semibold leading-8 tracking-tight text-gray-900"
                        >
                            {{ item.name }}
                        </p>
                        <p class="mt-1 text-base leading-7 text-gray-600">
                            {{ item.description }}
                        </p>
                    </div>
                </div>
            </div>
            <!-- Image section -->
            <div class="mt-32 sm:mt-40 xl:mx-auto xl:max-w-7xl xl:px-8">
                <img
                    src="https://logixpublic.s3.amazonaws.com/about/fireworkds.jpg"
                    alt=""
                    class="aspect-[5/2] w-full object-cover xl:rounded-3xl"
                />
            </div>

            <!-- Values section -->
            <div class="mx-auto mt-32 max-w-7xl px-6 sm:mt-40 lg:px-8">
                <div class="mx-auto max-w-2xl lg:mx-0">
                    <h2
                        class="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl"
                    >
                        Our values
                    </h2>
                    <p class="mt-6 text-lg leading-8 text-gray-600"></p>
                </div>
                <dl
                    class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 text-base leading-7 sm:grid-cols-2 lg:mx-0 lg:max-w-none lg:grid-cols-3"
                >
                    <div v-for="value in values" :key="value.name">
                        <dt class="font-semibold text-gray-900">
                            {{ value.name }}
                        </dt>
                        <dd class="mt-1 text-gray-600">
                            {{ value.description }}
                        </dd>
                    </div>
                </dl>
            </div>

            <!-- Logo cloud -->
            <div class="relative isolate -z-10 mb-40 mt-32 sm:mt-48">
                <div
                    class="absolute inset-x-0 top-1/2 -z-10 flex -translate-y-1/2 justify-center overflow-hidden [mask-image:radial-gradient(50%_45%_at_50%_55%,white,transparent)]"
                >
                    <svg
                        class="h-[40rem] w-[80rem] flex-none stroke-gray-200"
                        aria-hidden="true"
                    >
                        <defs>
                            <pattern
                                id="e9033f3e-f665-41a6-84ef-756f6778e6fe"
                                width="200"
                                height="200"
                                x="50%"
                                y="50%"
                                patternUnits="userSpaceOnUse"
                                patternTransform="translate(-100 0)"
                            >
                                <path d="M.5 200V.5H200" fill="none" />
                            </pattern>
                        </defs>
                        <svg
                            x="50%"
                            y="50%"
                            class="overflow-visible fill-gray-50"
                        >
                            <path
                                d="M-300 0h201v201h-201Z M300 200h201v201h-201Z"
                                stroke-width="0"
                            />
                        </svg>
                        <rect
                            width="100%"
                            height="100%"
                            stroke-width="0"
                            fill="url(#e9033f3e-f665-41a6-84ef-756f6778e6fe)"
                        />
                    </svg>
                </div>
                <div class="mx-auto max-w-7xl px-6 lg:px-8">
                    <h2
                        class="text-center text-lg font-semibold leading-8 text-gray-900"
                    >
                        You're in great company
                    </h2>
                    <div
                        class="mx-auto mt-10 grid max-w-lg grid-cols-1 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-3"
                    >
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/about/mm.jpg"
                            alt="double-m"
                            width="158"
                            height="48"
                        />
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/about/spruce.jpg"
                            alt="aircraft-spruce"
                            width="158"
                            height="48"
                        />
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/about/sweeps_logo.png"
                            alt="aopa-sweeps"
                            width="158"
                            height="48"
                        />
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/home/airsync_logo_tm_gray.png"
                            alt="airsync"
                            width="158"
                            height="48"
                        />
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/home/airbly_gray.png"
                            alt="airbly"
                            width="158"
                            height="48"
                        />
                        <img
                            class="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
                            src="https://logixpublic.s3.amazonaws.com/home/FA_Logo_BLK.jpg"
                            alt="airbly"
                            width="158"
                            height="48"
                        />
                    </div>
                </div>
            </div>
        </div>
    </LandingPageLayout>
</template>
